
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Wallet, Loader2, AlertTriangle } from 'lucide-react';
import { StripeBalance } from '@/types/stripe-finances';
import { Skeleton } from '@/components/ui/skeleton';

interface StripeBalanceProps {
  isLoading: boolean;
  error: Error | null;
  balance: StripeBalance | null;
}

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const StripeBalanceCard: React.FC<StripeBalanceProps> = ({ isLoading, error, balance }) => {
  const { t } = useTranslation('finances');

  const getAvailableBalance = () => {
    if (!balance?.available?.length) return null;
    // Assuming we're only displaying the first currency (usually USD)
    const firstAvailable = balance.available[0];
    return {
      amount: firstAvailable.amount,
      currency: firstAvailable.currency
    };
  };

  const getPendingBalance = () => {
    if (!balance?.pending?.length) return null;
    // Assuming we're only displaying the first currency (usually USD)
    const firstPending = balance.pending[0];
    return {
      amount: firstPending.amount,
      currency: firstPending.currency
    };
  };

  const availableBalance = getAvailableBalance();
  const pendingBalance = getPendingBalance();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-xl">
          <Wallet className="mr-2 h-5 w-5" />
          {t('balance.title')}
        </CardTitle>
        <CardDescription>
          {t('balance.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {error && (
          <div className="flex items-center text-red-500">
            <AlertTriangle className="mr-2 h-5 w-5" />
            <span>{t('balance.failedToLoad')}</span>
          </div>
        )}

        {!isLoading && !error && balance && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-100 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-500 mb-1">{t('balance.available')}</div>
              <div className="text-2xl font-semibold text-green-600">
                {availableBalance ? formatCurrency(availableBalance.amount, availableBalance.currency) : '$0.00'}
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-500 mb-1">{t('balance.pending')}</div>
              <div className="text-2xl font-semibold text-amber-600">
                {pendingBalance ? formatCurrency(pendingBalance.amount, pendingBalance.currency) : '$0.00'}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StripeBalanceCard;
